/**
 * PORTED VERBATIM from vsm-api-v2 src/services/accessLaunchUrl.ts (VSNP-1938): keep the two in step.
 * The portal shows the API's `launchUrl` (the admin app DTO carries it); this copy exists only to
 * PREVIEW the URL while an app is being edited, before the API has derived it.
 *
 * The launch URL of an Access app (VSNP-1938, doc 28f §4).
 *
 * DERIVED, never stored (owner decision Q3): there is no `launch_url` column.
 * One pure function, used by the client's app list (POST
 * /api/v2/access/devices/me/apps) and by the admin app DTO, and ported
 * verbatim to vsm-web-v2 (src/utils/accessLaunchUrl.ts) for the preview while
 * editing. The first rule that applies wins:
 *
 * | app                                                   | launchUrl                                   |
 * |-------------------------------------------------------|---------------------------------------------|
 * | external, first fqdn pattern without `*`              | https://<fqdn>/                             |
 * | external, only wildcard patterns                      | null                                        |
 * | public_hostname set (any other type)                  | https://<host>[:port]/, http:// on port 80  |
 * | http / https: target_host, else a single-IP cidr      | <type>://<host>[:port]/                     |
 * | subnet, single IP, port 80 / 443                      | http://<ip>/ / https://<ip>/                |
 * | subnet, single IP, another or no port                 | null                                        |
 * | wider subnet, tcp, udp                                | null                                        |
 *
 * A port is printed unless it is the scheme's default (80 for http, 443 for
 * https): an http app on 443 is http://host:443/, because dropping the port
 * would point the browser at 80. A null URL is "Copy address" in the client,
 * which never guesses a scheme the server did not give.
 *
 * The result is only ever `scheme://host[:port]/`: the host is checked by the
 * URL parser and anything that does not round-trip (a path, credentials, a
 * stray character in an admin-entered name) gives null rather than a URL the
 * Open button would follow somewhere unexpected.
 */

export interface LaunchableApp {
  app_type: string;
  target_host?: string | null;
  target_port?: number | null;
  target_cidr?: string | null;
  public_hostname?: string | null;
  fqdn_patterns?: unknown;
}

function patternsOf(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim());
  if (typeof v === 'string') {
    try {
      return patternsOf(JSON.parse(v));
    } catch {
      return v.trim() ? [v.trim()] : [];
    }
  }
  return [];
}

/** The single address of a /32 (IPv4) or /128 (IPv6) CIDR, else null. */
export function singleIpOf(cidr: string | null | undefined): string | null {
  const c = cidr?.trim();
  if (!c) return null;
  const slash = c.indexOf('/');
  const ip = slash < 0 ? c : c.slice(0, slash);
  const bits = slash < 0 ? null : c.slice(slash + 1);
  const v6 = ip.includes(':');
  if (bits !== null && bits !== (v6 ? '128' : '32')) return null;
  if (v6 ? !/^[0-9A-Fa-f:.]+$/.test(ip) : !/^\d{1,3}(\.\d{1,3}){3}$/.test(ip)) return null;
  if (!v6 && ip.split('.').some((o) => Number(o) > 255)) return null;
  return ip;
}

function validPort(p: number | null | undefined): number | undefined {
  return typeof p === 'number' && Number.isInteger(p) && p > 0 && p < 65536 ? p : undefined;
}

/** `scheme://host[:port]/`, or null when the host does not survive the URL parser unchanged. */
function urlOf(scheme: 'http' | 'https', host: string, port: number | undefined): string | null {
  const h = host.trim();
  if (!h || /[\s/?#@\\]/.test(h)) return null;
  const authority = h.includes(':') && !h.startsWith('[') ? `[${h}]` : h;
  const defaultPort = scheme === 'http' ? 80 : 443;
  const portPart = port !== undefined && port !== defaultPort ? `:${port}` : '';
  const candidate = `${scheme}://${authority}${portPart}/`;
  try {
    const u = new URL(candidate);
    if (u.protocol !== `${scheme}:` || u.pathname !== '/' || u.search || u.hash || u.username || u.password) return null;
    return u.toString();
  } catch {
    return null;
  }
}

/** The URL the client's Open button uses, or null for "Copy address". */
export function launchUrlFor(app: LaunchableApp): string | null {
  const port = validPort(app.target_port);
  if (app.app_type === 'external') {
    const fqdn = patternsOf(app.fqdn_patterns).find((p) => !p.includes('*'));
    return fqdn ? urlOf('https', fqdn, undefined) : null;
  }
  const pub = app.public_hostname?.trim();
  if (pub) return urlOf(port === 80 ? 'http' : 'https', pub, port === 80 ? undefined : port);
  if (app.app_type === 'http' || app.app_type === 'https') {
    const host = app.target_host?.trim() || singleIpOf(app.target_cidr);
    return host ? urlOf(app.app_type, host, port) : null;
  }
  if (app.app_type === 'subnet') {
    const ip = singleIpOf(app.target_cidr);
    if (!ip) return null;
    if (port === 80) return urlOf('http', ip, undefined);
    if (port === 443) return urlOf('https', ip, undefined);
    return null;
  }
  return null;
}
